import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, useAbruf, type ApiFehler } from '../api'
import { Angabe, Fehleranzeige, Karte, Tabelle, Zustand } from '../bausteine'
import { datum, geld, menge } from '../formate'
import { Statusmerkmal } from './Vertraege'
import { Abrechnungsbereitschaft } from '../abrechnungsbereitschaft'

interface Vertragsakte {
  id: string
  vertragsnummer: string
  kundennummer: string | null
  kundenname: string | null
  status: string
  produktId: string | null
  lieferbeginn: string
  lieferende: string | null
  abrechnungsturnus: string
  abschlagsturnus: string
  zahlungsart: string
  mindestlaufzeitMonate: number | null
  kuendigungsfristWochen: number | null
  verlaengerungMonate: number | null
  widerrufsfristEnde: string | null
  widerrufenAm: string | null
  kuendigungEingegangenAm: string | null
  kuendigungZum: string | null
  vertriebsweg: string | null
  lieferstelle: {
    marktlokation: string
    netzbetreiber: string | null
    anschrift: string | null
    bilanzierungsmethode: string
    lastprofil: string | null
    jahresverbrauchKwh: string | null
  } | null
}

interface Rechnungszeile {
  id: string
  rechnungsnummer: string
  art: string
  rechnungsdatum: string
  zeitraumVon: string
  zeitraumBis: string
  bruttobetrag: string
  zahlbetrag: string
}

interface Abschlagsplan {
  gueltigAb: string
  gueltigBis?: string | null
  turnus: string
  betragBrutto: string
  faelligkeitstag: number
  grundlageKwh: string | null
  grundlageHerkunft: string | null
  abschlaege: { id: string; faelligAm: string; betrag: string; status: string }[]
}

export default function Vertragsakte() {
  const { vertragsnummer } = useParams()
  const akte = useAbruf<Vertragsakte>(`/api/vertraege/${vertragsnummer}`)
  const rechnungen = useAbruf<Rechnungszeile[]>(
    akte.daten ? `/api/rechnungen/zu-vertrag/${akte.daten.id}` : undefined,
    [akte.daten?.id],
  )
  const plaene = useAbruf<Abschlagsplan[]>(
    akte.daten ? `/api/abschlaege/zu-vertrag/${akte.daten.id}` : undefined,
    [akte.daten?.id],
  )

  const [eingangAm, setzeEingangAm] = useState(new Date().toISOString().slice(0, 10))
  const [umzug, setzeUmzug] = useState(false)
  const [fehler, setzeFehler] = useState<ApiFehler>()
  const [meldung, setzeMeldung] = useState<string>()

  async function kuendigen() {
    setzeFehler(undefined)
    setzeMeldung(undefined)
    try {
      const ergebnis = await api.sende<Vertragsakte>(
        `/api/vertraege/${vertragsnummer}/kuendigung`,
        { eingegangenAm: eingangAm, umzug },
      )
      setzeMeldung(`Der Vertrag endet zum ${datum(ergebnis.kuendigungZum)}.`)
      akte.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    }
  }

  // Die Anwendung laesst leere Felder weg; ein Vergleich auf null ginge
  // daher ins Leere - der geltende Plan hat schlicht kein Ende.
  const geltenderPlan = (plaene.daten ?? []).find((plan) => !plan.gueltigBis)

  return (
    <>
      <h1>Vertrag {vertragsnummer}</h1>
      <p className="untertitel">
        {akte.daten?.kundenname}{' '}
        {akte.daten?.kundennummer && (
          <Link to={`/kunden/${akte.daten.kundennummer}`}>({akte.daten.kundennummer})</Link>
        )}
      </p>

      <Zustand laeuft={akte.laeuft} fehler={akte.fehler}>
        {akte.daten && (
          <>
            {akte.daten.widerrufenAm && (
              <div className="hinweis warnung">
                Der Vertrag wurde am {datum(akte.daten.widerrufenAm)} widerrufen.
              </div>
            )}

            <Karte titel="Vertragsdaten">
              <div className="angaben">
                <Angabe beschriftung="Status">
                  <Statusmerkmal status={akte.daten.status} />
                </Angabe>
                <Angabe beschriftung="Lieferbeginn">{datum(akte.daten.lieferbeginn)}</Angabe>
                <Angabe beschriftung="Lieferende">{datum(akte.daten.lieferende)}</Angabe>
                <Angabe beschriftung="Abrechnungsturnus">{akte.daten.abrechnungsturnus}</Angabe>
                <Angabe beschriftung="Abschlagsturnus">{akte.daten.abschlagsturnus}</Angabe>
                <Angabe beschriftung="Zahlungsart">{akte.daten.zahlungsart}</Angabe>
                <Angabe beschriftung="Mindestlaufzeit">
                  {akte.daten.mindestlaufzeitMonate
                    ? `${akte.daten.mindestlaufzeitMonate} Monate`
                    : '—'}
                </Angabe>
                <Angabe beschriftung="Kündigungsfrist">
                  {akte.daten.kuendigungsfristWochen
                    ? `${akte.daten.kuendigungsfristWochen} Wochen`
                    : '—'}
                </Angabe>
                <Angabe beschriftung="Widerrufsfrist bis">
                  {datum(akte.daten.widerrufsfristEnde)}
                </Angabe>
                <Angabe beschriftung="Vertriebsweg">{akte.daten.vertriebsweg ?? '—'}</Angabe>
              </div>
            </Karte>

            {akte.daten.lieferstelle && (
              <Karte titel="Lieferstelle">
                <div className="angaben">
                  <Angabe beschriftung="Marktlokation">
                    {akte.daten.lieferstelle.marktlokation}
                  </Angabe>
                  <Angabe beschriftung="Netzbetreiber">
                    {akte.daten.lieferstelle.netzbetreiber ?? '—'}
                  </Angabe>
                  <Angabe beschriftung="Anschrift">
                    {akte.daten.lieferstelle.anschrift ?? '—'}
                  </Angabe>
                  <Angabe beschriftung="Bilanzierung">
                    {akte.daten.lieferstelle.bilanzierungsmethode}
                  </Angabe>
                  <Angabe beschriftung="Lastprofil">
                    {akte.daten.lieferstelle.lastprofil ?? '—'}
                  </Angabe>
                  <Angabe beschriftung="Jahresverbrauch">
                    {menge(akte.daten.lieferstelle.jahresverbrauchKwh)}
                  </Angabe>
                </div>
              </Karte>
            )}

            {/* Neben dem Status: reichen die Stammdaten fuer die Abrechnung? */}
            <Abrechnungsbereitschaft vertragsnummer={akte.daten.vertragsnummer} />

            <Karte titel="Kündigung erfassen">
              {meldung && <div className="hinweis info">{meldung}</div>}
              {fehler && <Fehleranzeige fehler={fehler} />}
              {akte.daten.kuendigungZum ? (
                <div className="angaben">
                  <Angabe beschriftung="Eingegangen am">
                    {datum(akte.daten.kuendigungEingegangenAm)}
                  </Angabe>
                  <Angabe beschriftung="Wirksam zum">{datum(akte.daten.kuendigungZum)}</Angabe>
                </div>
              ) : (
                <div className="leiste" style={{ marginBottom: 0 }}>
                  <div className="feld">
                    <label htmlFor="eingang">Eingegangen am</label>
                    <input
                      id="eingang"
                      type="date"
                      value={eingangAm}
                      onChange={(ereignis) => setzeEingangAm(ereignis.target.value)}
                    />
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                      fontSize: 14,
                      paddingBottom: 7,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={umzug}
                      onChange={(ereignis) => setzeUmzug(ereignis.target.checked)}
                    />
                    Umzug (sechs Wochen nach Paragraf 41b EnWG)
                  </label>
                  <button className="betont" onClick={kuendigen}>
                    Kündigung erfassen
                  </button>
                </div>
              )}
            </Karte>

            <Karte titel="Abschlagsplan">
              <Zustand
                laeuft={plaene.laeuft}
                fehler={plaene.fehler}
                leer={!geltenderPlan}
                leertext="Kein geltender Abschlagsplan."
              >
                {geltenderPlan && (
                  <>
                    <div className="angaben" style={{ marginBottom: 14 }}>
                      <Angabe beschriftung="Gültig ab">
                        {datum(geltenderPlan.gueltigAb)}
                      </Angabe>
                      <Angabe beschriftung="Turnus">{geltenderPlan.turnus}</Angabe>
                      <Angabe beschriftung="Betrag">{geld(geltenderPlan.betragBrutto)}</Angabe>
                      <Angabe beschriftung="Grundlage">
                        {menge(geltenderPlan.grundlageKwh)}
                      </Angabe>
                      <Angabe beschriftung="Herkunft der Grundlage">
                        {geltenderPlan.grundlageHerkunft ?? '—'}
                      </Angabe>
                    </div>
                    <Tabelle
                      spalten={[
                        { name: 'Fällig am' },
                        { name: 'Betrag', zahl: true },
                        { name: 'Status' },
                      ]}
                    >
                      {geltenderPlan.abschlaege.map((abschlag) => (
                        <tr key={abschlag.id}>
                          <td>{datum(abschlag.faelligAm)}</td>
                          <td className="zahl">{geld(abschlag.betrag)}</td>
                          <td>{abschlag.status}</td>
                        </tr>
                      ))}
                    </Tabelle>
                  </>
                )}
              </Zustand>
            </Karte>

            <Karte titel="Rechnungen">
              <Zustand
                laeuft={rechnungen.laeuft}
                fehler={rechnungen.fehler}
                leer={(rechnungen.daten ?? []).length === 0}
                leertext="Noch keine Rechnung."
              >
                <Tabelle
                  spalten={[
                    { name: 'Rechnungsnummer' },
                    { name: 'Art' },
                    { name: 'Zeitraum' },
                    { name: 'Brutto', zahl: true },
                    { name: 'Zahlbetrag', zahl: true },
                  ]}
                >
                  {(rechnungen.daten ?? []).map((rechnung) => (
                    <tr key={rechnung.id}>
                      <td>
                        <Link to={`/rechnungen/${rechnung.rechnungsnummer}`}>
                          {rechnung.rechnungsnummer}
                        </Link>
                      </td>
                      <td>{rechnung.art}</td>
                      <td>
                        {datum(rechnung.zeitraumVon)} – {datum(rechnung.zeitraumBis)}
                      </td>
                      <td className="zahl">{geld(rechnung.bruttobetrag)}</td>
                      <td className="zahl">{geld(rechnung.zahlbetrag)}</td>
                    </tr>
                  ))}
                </Tabelle>
              </Zustand>
            </Karte>
          </>
        )}
      </Zustand>
    </>
  )
}
